import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import PaymentsReportsPanel from './components/PaymentsReportsPanel';
import PaymentsOverviewPanel from './components/PaymentsOverviewPanel';
import TrafficReportsPanel from './components/TrafficReportsPanel';
import PlatformSettingsPanel from './components/PlatformSettingsPanel';
import ContactSubmissionsPanel from './components/ContactSubmissionsPanel';
import EmailCampaignsPanel from './components/EmailCampaignsPanel';

type PlatformStats = {
  total_schools: number;
  total_users: number;
  total_teachers: number;
  total_students: number;
  total_parents: number;
  total_admins: number;
};

type SchoolRow = {
  id: number;
  name: string;
  county: string;
  city: string;
  education_level?: string | null;
  teachers: number;
  students: number;
  parents: number;
  admins: number;
};

type UserRow = {
  id: number;
  full_name: string;
  email: string;
  role: string;
  school_id: number | null;
  school_name: string | null;
  county: string | null;
  city: string | null;
  is_suspended: boolean;
  is_platform_admin: boolean;
  subscription_plan?: string | null;
  subscription_unrestricted?: boolean;
  subscription_expires_at?: string | null;
};

type SecretariatUserRow = {
  id: number;
  full_name: string;
  email: string;
  created_at?: string | null;
  is_suspended?: boolean;
};

type SchoolAdminAccountRow = {
  id: number;
  full_name: string;
  email: string;
  created_at?: string | null;
  is_suspended?: boolean;
};

type PagedResponse<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};

type Summary = {
  school: { id: number; name: string; county: string; city: string };
  counts: { teachers: number; students: number; parents: number; admins: number; classes: number };
};

type SchoolSettings = {
  school_id: number;
  school_mode: 'b2c' | 'b2b';
};

type SchoolClasses = {
  school_classes: Array<{
    id: number;
    name: string;
    rating_percent?: number | null;
    avg_grade?: number | null;
    grades_count?: number;
  }>;
  classrooms: Array<{
    id: number;
    name: string;
    admin_id: number | null;
    school_class_id?: number | null;
    admins?: Array<{ id: number; full_name: string; email: string; role: string }>;
  }>;
};

type AccessGrant = {
  id: number;
  scope_type: 'school' | 'school_class' | 'classroom' | 'user' | string;
  scope_id: number;
  kind: 'unrestricted' | string;
  enabled: boolean;
  note?: string | null;
  updated_at?: string | null;
};

type TabKey = 'overview' | 'schools' | 'users' | 'email' | 'contact' | 'reports' | 'settings';

const roleOptions = [
  { value: '', label: 'Toate rolurile' },
  { value: 'profesor', label: 'Profesori' },
  { value: 'elev', label: 'Elevi' },
  { value: 'parinte', label: 'Părinți' },
  { value: 'admin', label: 'Admin' },
];

const educationLevelOptions = [
  { value: 'gimnazial', label: 'Gimnazial' },
  { value: 'liceal', label: 'Liceal' },
  { value: 'universitar', label: 'Universitar' },
];

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
        active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-lg font-semibold text-foreground">{title}</div>
            {subtitle ? <div className="text-sm text-muted-foreground">{subtitle}</div> : null}
          </div>
          <button
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
            onClick={onClose}
            aria-label="Închide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function PlatformAdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, uiRole, signOut } = useAuth();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);

  const user = useMemo(() => {
    return {
      role: (uiRole || 'admin') as 'admin' | 'teacher' | 'student' | 'parent',
      name: me?.full_name || 'Administrator platformă',
      avatar: me?.avatar_url || undefined,
      is_platform_admin: Boolean((me as any)?.is_platform_admin),
    };
  }, [me?.avatar_url, me?.full_name, uiRole]);

  const tab = useMemo<TabKey>(() => {
    const params = new URLSearchParams(location.search);
    const t = (params.get('tab') || 'overview') as TabKey;
    return (['overview', 'schools', 'users', 'email', 'contact', 'reports', 'settings'] as TabKey[]).includes(t) ? t : 'overview';
  }, [location.search]);

  const [stats, setStats] = useState<PlatformStats | null>(null);

  const [schoolQ, setSchoolQ] = useState('');
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const selectedSchool = useMemo(
    () => schools.find((s) => s.id === selectedSchoolId) || null,
    [schools, selectedSchoolId]
  );

  const [counties, setCounties] = useState<string[]>([]);
  const [localities, setLocalities] = useState<string[]>([]);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);
  const [createSchoolOpen, setCreateSchoolOpen] = useState(false);
  const [createSchoolName, setCreateSchoolName] = useState('');
  const [createSchoolCounty, setCreateSchoolCounty] = useState('');
  const [createSchoolCity, setCreateSchoolCity] = useState('');
  const [createSchoolEducationLevel, setCreateSchoolEducationLevel] = useState('');

  const [schoolSummary, setSchoolSummary] = useState<Summary | null>(null);
  const [schoolUsers, setSchoolUsers] = useState<UserRow[]>([]);
  const [schoolUsersRole, setSchoolUsersRole] = useState('');
  const [schoolUsersQ, setSchoolUsersQ] = useState('');
  const [schoolClasses, setSchoolClasses] = useState<SchoolClasses | null>(null);
  const [schoolAccessGrants, setSchoolAccessGrants] = useState<AccessGrant[]>([]);
  const [savingGrant, setSavingGrant] = useState(false);
  const [schoolSettings, setSchoolSettings] = useState<SchoolSettings | null>(null);
  const [savingSchoolMode, setSavingSchoolMode] = useState(false);
  const [schoolModeDraft, setSchoolModeDraft] = useState<'b2c' | 'b2b'>('b2c');

  const [secretariatUsers, setSecretariatUsers] = useState<SecretariatUserRow[]>([]);
  const [secretariatLoading, setSecretariatLoading] = useState(false);
  const [secretariatCreateLoading, setSecretariatCreateLoading] = useState(false);
  const [secretariatNewName, setSecretariatNewName] = useState('');
  const [secretariatNewEmail, setSecretariatNewEmail] = useState('');

  const [schoolAdmins, setSchoolAdmins] = useState<SchoolAdminAccountRow[]>([]);
  const [schoolAdminsLoading, setSchoolAdminsLoading] = useState(false);

  const [globalUsers, setGlobalUsers] = useState<UserRow[]>([]);
  const [globalUsersTotal, setGlobalUsersTotal] = useState(0);
  const [globalUsersPage, setGlobalUsersPage] = useState(1);
  const globalUsersPageSize = 10;
  const [globalUsersRole, setGlobalUsersRole] = useState('');
  const [globalUsersQ, setGlobalUsersQ] = useState('');
  const [globalUserDetailsOpen, setGlobalUserDetailsOpen] = useState<Record<number, boolean>>({});
  const [globalUserDetailsLoading, setGlobalUserDetailsLoading] = useState<Record<number, boolean>>({});
  const [globalUserDetails, setGlobalUserDetails] = useState<Record<number, any>>({});

  const [createSchoolAdminOpen, setCreateSchoolAdminOpen] = useState(false);
  const [schoolAdminEmail, setSchoolAdminEmail] = useState('');
  const [schoolAdminName, setSchoolAdminName] = useState('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/admin/platform/stats');
      setStats(resp.data as any);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSchools = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/admin/platform/schools', {
        params: { q: schoolQ || undefined },
      });
      const list = Array.isArray(resp.data) ? (resp.data as any[]) : [];
      setSchools(
        list.map((s) => ({
          id: Number(s.id),
          name: String(s.name || ''),
          county: String(s.county || ''),
          city: String(s.city || ''),
          teachers: Number(s.teachers || 0),
          students: Number(s.students || 0),
          parents: Number(s.parents || 0),
          admins: Number(s.admins || 0),
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [schoolQ]);

  const loadCounties = useCallback(async () => {
    try {
      const resp = await api.get('/api/locatii/counties');
      setCounties(Array.isArray(resp.data) ? (resp.data as string[]) : []);
    } catch {
      setCounties([]);
    }
  }, []);

  const loadLocalities = useCallback(async (county: string) => {
    const c = (county || '').trim();
    if (!c) {
      setLocalities([]);
      return;
    }
    setIsLoadingLocalities(true);
    try {
      const resp = await api.get('/api/locatii/localities', { params: { county: c } });
      setLocalities(Array.isArray(resp.data) ? (resp.data as string[]) : []);
    } catch {
      setLocalities([]);
    } finally {
      setIsLoadingLocalities(false);
    }
  }, []);

  const loadSelectedSchoolDetails = useCallback(async () => {
    if (!selectedSchoolId) return;
    setLoading(true);
    try {
      const [summaryResp, usersResp, classesResp, grantsResp, settingsResp, secretariatResp, schoolAdminsResp] = await Promise.all([
        api.get('/api/admin/school/summary', { params: { school_id: selectedSchoolId } }),
        api.get('/api/admin/school/users', {
          params: {
            school_id: selectedSchoolId,
            role: schoolUsersRole || undefined,
            q: schoolUsersQ || undefined,
          },
        }),
        api.get('/api/admin/school/classes', { params: { school_id: selectedSchoolId } }),
        api.get('/api/admin/platform/access-grants', { params: { school_id: selectedSchoolId } }),
        api.get('/api/admin/school/settings', { params: { school_id: selectedSchoolId } }),
        api.get('/api/admin/school/secretariat-users', { params: { school_id: selectedSchoolId } }),
        api.get(`/api/admin/platform/schools/${selectedSchoolId}/admins`),
      ]);
      setSchoolSummary(summaryResp.data as any);
      setSchoolUsers(Array.isArray(usersResp.data) ? (usersResp.data as any) : []);
      setSchoolClasses(classesResp.data as any);
      setSchoolAccessGrants(Array.isArray(grantsResp.data) ? (grantsResp.data as any) : []);
      const settings = (settingsResp.data || {}) as SchoolSettings;
      setSchoolSettings(settings);
      setSchoolModeDraft((settings.school_mode || 'b2c') as any);
      setSecretariatUsers(Array.isArray(secretariatResp.data) ? (secretariatResp.data as any) : []);
      setSchoolAdmins(Array.isArray(schoolAdminsResp.data) ? (schoolAdminsResp.data as any) : []);
    } finally {
      setLoading(false);
    }
  }, [schoolUsersQ, schoolUsersRole, selectedSchoolId]);

  const loadSecretariatUsers = useCallback(async () => {
    if (!selectedSchoolId) return;
    setSecretariatLoading(true);
    try {
      const resp = await api.get('/api/admin/school/secretariat-users', { params: { school_id: selectedSchoolId } });
      setSecretariatUsers(Array.isArray(resp.data) ? (resp.data as any) : []);
    } catch {
      setSecretariatUsers([]);
    } finally {
      setSecretariatLoading(false);
    }
  }, [selectedSchoolId]);

  const loadSchoolAdmins = useCallback(async () => {
    if (!selectedSchoolId) return;
    setSchoolAdminsLoading(true);
    try {
      const resp = await api.get(`/api/admin/platform/schools/${selectedSchoolId}/admins`);
      setSchoolAdmins(Array.isArray(resp.data) ? (resp.data as any) : []);
    } catch {
      setSchoolAdmins([]);
    } finally {
      setSchoolAdminsLoading(false);
    }
  }, [selectedSchoolId]);

  const createSecretariatUser = useCallback(async () => {
    if (!selectedSchoolId) return;
    const email = secretariatNewEmail.trim().toLowerCase();
    const full_name = secretariatNewName.trim();
    if (!email || !email.includes('@')) {
      alert('Email invalid.');
      return;
    }

    setSecretariatCreateLoading(true);
    try {
      await api.post(
        '/api/admin/school/secretariat-users',
        { email, full_name: full_name || undefined },
        { params: { school_id: selectedSchoolId } }
      );
      setSecretariatNewEmail('');
      setSecretariatNewName('');
      await loadSecretariatUsers();
      alert('Cont secretariat creat. Parola temporară a fost trimisă pe email și trebuie schimbată la primul login.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea contul.');
    } finally {
      setSecretariatCreateLoading(false);
    }
  }, [loadSecretariatUsers, secretariatNewEmail, secretariatNewName, selectedSchoolId]);

  const resendSecretariatInvite = useCallback(
    async (u: SecretariatUserRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Retrimiți invitația pentru ${u.full_name} (${u.email})? Se resetează parola.`);
      if (!ok) return;
      try {
        await api.post(`/api/admin/school/secretariat-users/${u.id}/resend-invite`, {}, { params: { school_id: selectedSchoolId } });
        await loadSecretariatUsers();
        alert('Invitația a fost retrimisă.');
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut retrimite invitația.');
      }
    },
    [loadSecretariatUsers, selectedSchoolId]
  );

  const deleteSecretariatUser = useCallback(
    async (u: SecretariatUserRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Ștergi contul de secretariat pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.delete(`/api/admin/school/secretariat-users/${u.id}`, { params: { school_id: selectedSchoolId } });
        await loadSecretariatUsers();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut șterge contul.');
      }
    },
    [loadSecretariatUsers, selectedSchoolId]
  );

  const activateSecretariatUser = useCallback(
    async (u: SecretariatUserRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Activezi contul de secretariat pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.post(`/api/admin/school/users/${u.id}/suspend`, { is_suspended: false }, { params: { school_id: selectedSchoolId } });
        await loadSecretariatUsers();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut activa contul.');
      }
    },
    [loadSecretariatUsers, selectedSchoolId]
  );

  const suspendSecretariatUser = useCallback(
    async (u: SecretariatUserRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Suspendi contul de secretariat pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.post(`/api/admin/school/users/${u.id}/suspend`, { is_suspended: true }, { params: { school_id: selectedSchoolId } });
        await loadSecretariatUsers();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut suspenda contul.');
      }
    },
    [loadSecretariatUsers, selectedSchoolId]
  );

  const resendSchoolAdminInvite = useCallback(
    async (u: SchoolAdminAccountRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Retrimiți invitația pentru ${u.full_name} (${u.email})? Se resetează parola.`);
      if (!ok) return;
      try {
        await api.post(`/api/admin/platform/schools/${selectedSchoolId}/admins/${u.id}/resend-invite`, {});
        await loadSchoolAdmins();
        alert('Invitația a fost retrimisă.');
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut retrimite invitația.');
      }
    },
    [loadSchoolAdmins, selectedSchoolId]
  );

  const deleteSchoolAdmin = useCallback(
    async (u: SchoolAdminAccountRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Ștergi contul de admin școală pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.delete(`/api/admin/platform/schools/${selectedSchoolId}/admins/${u.id}`);
        await loadSchoolAdmins();
        await loadSelectedSchoolDetails();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut șterge contul.');
      }
    },
    [loadSchoolAdmins, loadSelectedSchoolDetails, selectedSchoolId]
  );

  const activateSchoolAdmin = useCallback(
    async (u: SchoolAdminAccountRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Activezi contul de admin È™coalÄƒ pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.post(`/api/admin/school/users/${u.id}/suspend`, { is_suspended: false });
        await loadSchoolAdmins();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut activa contul.');
      }
    },
    [loadSchoolAdmins, selectedSchoolId]
  );

  const suspendSchoolAdmin = useCallback(
    async (u: SchoolAdminAccountRow) => {
      if (!selectedSchoolId) return;
      const ok = window.confirm(`Suspendi contul de admin È™coalÄƒ pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.post(`/api/admin/school/users/${u.id}/suspend`, { is_suspended: true });
        await loadSchoolAdmins();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut suspenda contul.');
      }
    },
    [loadSchoolAdmins, selectedSchoolId]
  );

  const saveSchoolMode = useCallback(async () => {
    if (!selectedSchoolId) return;
    setSavingSchoolMode(true);
    try {
      await api.put(`/api/admin/platform/schools/${selectedSchoolId}/mode`, { school_mode: schoolModeDraft });
      await loadSelectedSchoolDetails();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut salva modul școlii.');
    } finally {
      setSavingSchoolMode(false);
    }
  }, [loadSelectedSchoolDetails, schoolModeDraft, selectedSchoolId]);

  const loadGlobalUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/admin/platform/users', {
        params: {
          q: globalUsersQ || undefined,
          role: globalUsersRole || undefined,
          page: globalUsersPage,
          page_size: globalUsersPageSize,
        },
      });
      const data = (resp.data || {}) as PagedResponse<UserRow>;
      setGlobalUsers(Array.isArray(data.items) ? data.items : []);
      setGlobalUsersTotal(Number(data.total || 0));
    } finally {
      setLoading(false);
    }
  }, [globalUsersQ, globalUsersRole, globalUsersPage]);

  const loadGlobalUserDetails = useCallback(async (userId: number) => {
    const id = Number(userId);
    if (!id) return;
    if (globalUserDetailsLoading[id]) return;
    setGlobalUserDetailsLoading((p) => ({ ...p, [id]: true }));
    try {
      const resp = await api.get(`/api/admin/platform/users/${id}/details`, { params: { login_limit: 50, consent_limit: 50 } });
      setGlobalUserDetails((p) => ({ ...p, [id]: resp.data }));
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca detaliile utilizatorului.');
    } finally {
      setGlobalUserDetailsLoading((p) => ({ ...p, [id]: false }));
    }
  }, [globalUserDetailsLoading]);

  const exportGlobalUserAudit = useCallback(async (userId: number) => {
    const id = Number(userId);
    if (!id) return;
    try {
      const resp = await api.get(`/api/admin/platform/users/${id}/export.json`, { responseType: 'blob' as any });
      const blob = new Blob([resp.data], { type: 'application/json;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_${id}_audit.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta datele.');
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (tab === 'schools' || tab === 'overview') void loadSchools();
    if (tab === 'users') void loadGlobalUsers();
  }, [loadGlobalUsers, loadSchools, tab]);

  useEffect(() => {
    // reset paging on filter changes
    setGlobalUsersPage(1);
  }, [globalUsersQ, globalUsersRole]);

  useEffect(() => {
    if (selectedSchoolId && tab === 'schools') void loadSelectedSchoolDetails();
  }, [loadSelectedSchoolDetails, selectedSchoolId, tab]);

  const isGrantEnabled = useCallback(
    (scopeType: string, scopeId: number) => {
      const g = schoolAccessGrants.find((x) => x.kind === 'unrestricted' && x.scope_type === scopeType && Number(x.scope_id) === Number(scopeId));
      return Boolean(g?.enabled);
    },
    [schoolAccessGrants]
  );

  const setGrant = useCallback(
    async (scopeType: 'school' | 'school_class' | 'classroom' | 'user', scopeId: number, enabled: boolean) => {
      if (!selectedSchoolId) return;
      setSavingGrant(true);
      try {
        await api.put('/api/admin/platform/access-grants', {
          scope_type: scopeType,
          scope_id: scopeId,
          kind: 'unrestricted',
          enabled,
        });
        const resp = await api.get('/api/admin/platform/access-grants', { params: { school_id: selectedSchoolId } });
        setSchoolAccessGrants(Array.isArray(resp.data) ? (resp.data as any) : []);
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut salva licența.');
      } finally {
        setSavingGrant(false);
      }
    },
    [selectedSchoolId]
  );

  useEffect(() => {
    if (createSchoolOpen) void loadCounties();
  }, [createSchoolOpen, loadCounties]);

  useEffect(() => {
    if (!createSchoolOpen) return;
    const c = (createSchoolCounty || '').trim();
    if (!c) {
      setLocalities([]);
      setCreateSchoolCity('');
      return;
    }
    void loadLocalities(c);
  }, [createSchoolCounty, createSchoolOpen, loadLocalities]);

  const suspendToggle = async (u: UserRow) => {
    const next = !u.is_suspended;
    const ok = window.confirm(`${next ? 'Suspendă' : 'Reactivează'} contul pentru ${u.full_name}?`);
    if (!ok) return;
    try {
      await api.post(`/api/admin/school/users/${u.id}/suspend`, { is_suspended: next });
      if (tab === 'users') await loadGlobalUsers();
      if (tab === 'schools') await loadSelectedSchoolDetails();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut actualiza statusul.');
    }
  };

  const deleteUser = async (u: UserRow) => {
    const ok = window.confirm(
      `Ștergi DEFINITIV contul „${u.full_name}” (${u.email})?\n\nAtenție: se vor șterge și date asociate.`
    );
    if (!ok) return;
    try {
      await api.delete(`/api/admin/platform/users/${u.id}`);
      if (tab === 'users') await loadGlobalUsers();
      if (tab === 'schools') await loadSelectedSchoolDetails();
      await loadStats();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut șterge utilizatorul.');
    }
  };

  const deleteSchool = async (s: SchoolRow) => {
    const ok = window.confirm(
      `Ștergi DEFINITIV școala „${s.name}” (${s.county}, ${s.city})?\n\nAtenție: se vor șterge și utilizatorii/clasele.`
    );
    if (!ok) return;
    try {
      await api.delete(`/api/admin/platform/schools/${s.id}`);
      setSelectedSchoolId(null);
      setSchoolSummary(null);
      setSchoolUsers([]);
      setSchoolClasses(null);
      await loadSchools();
      await loadStats();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut șterge școala.');
    }
  };

  const createSchool = async () => {
    if (
      !createSchoolName.trim() ||
      !createSchoolCounty.trim() ||
      !createSchoolCity.trim() ||
      !createSchoolEducationLevel.trim()
    ) {
      alert('Completează numele, județul, localitatea și forma de învățământ.');
      return;
    }
    try {
      const resp = await api.post('/api/admin/platform/schools', {
        name: createSchoolName.trim(),
        county: createSchoolCounty.trim(),
        city: createSchoolCity.trim(),
        education_level: createSchoolEducationLevel.trim(),
      });
      const id = Number(resp.data?.id);
      setCreateSchoolOpen(false);
      setCreateSchoolName('');
      setCreateSchoolCounty('');
      setCreateSchoolCity('');
      setCreateSchoolEducationLevel('');
      await loadSchools();
      await loadStats();
      if (id) setSelectedSchoolId(id);
      navigate('/platform-admin-dashboard?tab=schools');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea școala.');
    }
  };

  const createSchoolAdmin = async () => {
    if (!selectedSchoolId) return;
    if (!schoolAdminEmail.trim()) {
      alert('Completează email-ul și parola.');
      return;
    }
    try {
      await api.post(`/api/admin/platform/schools/${selectedSchoolId}/create-admin`, {
        email: schoolAdminEmail.trim(),
        full_name: schoolAdminName.trim() || undefined,
      });
      setCreateSchoolAdminOpen(false);
      setSchoolAdminEmail('');
      setSchoolAdminName('');
      await loadSelectedSchoolDetails();
      await loadSchools();
      await loadStats();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea administratorul de școală.');
    }
  };

  const deleteClassroom = async (id: number) => {
    const ok = window.confirm('Ștergi DEFINITIV această clasă?');
    if (!ok) return;
    try {
      await api.delete(`/api/admin/platform/classrooms/${id}`);
      await loadSelectedSchoolDetails();
      await loadStats();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut șterge clasa.');
    }
  };

  const deleteSchoolClass = async (id: number) => {
    const ok = window.confirm('Ștergi DEFINITIV această clasă (catalog)?');
    if (!ok) return;
    try {
      await api.delete(`/api/admin/platform/school-classes/${id}`);
      await loadSelectedSchoolDetails();
      await loadStats();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut șterge clasa (catalog).');
    }
  };

  return (
    <>
      <Helmet>
        <title>Administrare platformă - Scola</title>
      </Helmet>

      <Header user={user} onNavigate={(p) => navigate(p)} onSignOut={() => signOut()} />

      <div className="min-h-screen bg-background pt-16">
        <DashboardSidebar user={{ role: 'admin', is_platform_admin: Boolean((me as any)?.is_platform_admin) }} isCollapsed={isSidebarCollapsed} onNavigate={(p) => navigate(p)} />

        <main className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsSidebarCollapsed((p) => !p)}
                  className="hidden lg:flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-colors"
                  aria-label="Comută bara laterală"
                >
                  <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Administrare platformă</h1>
                  <p className="text-sm text-muted-foreground">Școli, utilizatori, rapoarte și setări</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" iconName="RefreshCw" onClick={() => void loadStats()} disabled={loading}>
                  Reîncarcă
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  onClick={() => {
                    setCreateSchoolOpen(true);
                    navigate('/platform-admin-dashboard?tab=schools');
                  }}
                >
                  Adaugă școală
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
              {[
                { label: 'Școli', value: stats?.total_schools ?? 0 },
                { label: 'Utilizatori', value: stats?.total_users ?? 0 },
                { label: 'Profesori', value: stats?.total_teachers ?? 0 },
                { label: 'Elevi', value: stats?.total_students ?? 0 },
                { label: 'Părinți', value: stats?.total_parents ?? 0 },
                { label: 'Admin', value: stats?.total_admins ?? 0 },
              ].map((c) => (
                <div key={c.label} className="bg-card border border-border rounded-lg p-5">
                  <div className="text-xs text-muted-foreground">{c.label}</div>
                  <div className="text-2xl font-bold text-foreground mt-1">{c.value}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <TabButton
                active={tab === 'overview'}
                label="Prezentare"
                onClick={() => navigate('/platform-admin-dashboard?tab=overview')}
              />
              <TabButton active={tab === 'schools'} label="Școli" onClick={() => navigate('/platform-admin-dashboard?tab=schools')} />
              <TabButton active={tab === 'users'} label="Utilizatori" onClick={() => navigate('/platform-admin-dashboard?tab=users')} />
              <TabButton active={tab === 'email'} label="Email" onClick={() => navigate('/platform-admin-dashboard?tab=email')} />
              <TabButton active={tab === 'contact'} label="Contact" onClick={() => navigate('/platform-admin-dashboard?tab=contact')} />
              <TabButton active={tab === 'reports'} label="Rapoarte" onClick={() => navigate('/platform-admin-dashboard?tab=reports')} />
              <TabButton active={tab === 'settings'} label="Setări" onClick={() => navigate('/platform-admin-dashboard?tab=settings')} />
            </div>

            {tab === 'overview' ? (
              <PaymentsOverviewPanel />
            ) : null}

            {tab === 'schools' ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-semibold text-foreground">Școli</div>
                    <Button variant="outline" size="sm" iconName="RefreshCw" onClick={() => void loadSchools()}>
                      Reîncarcă
                    </Button>
                  </div>

                  <Input
                    label="Caută"
                    placeholder="Nume școală..."
                    value={schoolQ}
                    onChange={(e) => setSchoolQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void loadSchools();
                    }}
                  />

                  <div className="mt-4 space-y-2 max-h-[560px] overflow-auto pr-1">
                    {schools.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Nu există școli.</div>
                    ) : null}
                    {schools.map((s) => {
                      const active = selectedSchoolId === s.id;
                      return (
                        <button
                          key={s.id}
                          onClick={() => setSelectedSchoolId(s.id)}
                          className={`w-full text-left border rounded-lg p-3 transition-colors ${
                            active ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted/40'
                          }`}
                        >
                          <div className="font-medium text-foreground">{s.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {s.county}, {s.city} • Prof: {s.teachers} • Elevi: {s.students}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4">
                  {selectedSchool ? (
                    <>
                      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                        <div>
                          <div className="text-lg font-semibold text-foreground">{selectedSchool.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {selectedSchool.county}, {selectedSchool.city}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" iconName="UserPlus" onClick={() => setCreateSchoolAdminOpen(true)}>
                            Admin școală
                          </Button>
                          <Button
                            variant="destructive"
                            iconName="Trash2"
                            onClick={() => void deleteSchool(selectedSchool)}
                          >
                            Șterge școala
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                        {[
                          { label: 'Profesori', value: schoolSummary?.counts.teachers ?? selectedSchool.teachers },
                          { label: 'Elevi', value: schoolSummary?.counts.students ?? selectedSchool.students },
                          { label: 'Părinți', value: schoolSummary?.counts.parents ?? selectedSchool.parents },
                          { label: 'Admin', value: schoolSummary?.counts.admins ?? selectedSchool.admins },
                          { label: 'Clase', value: schoolSummary?.counts.classes ?? 0 },
                        ].map((c) => (
                          <div key={c.label} className="border border-border rounded-lg p-3 bg-background">
                            <div className="text-xs text-muted-foreground">{c.label}</div>
                            <div className="text-xl font-bold text-foreground mt-1">{c.value}</div>
                          </div>
                        ))}
                      </div>

                      <div className="mb-4 border border-border rounded-lg p-3 bg-background">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <div className="text-sm font-semibold text-foreground">Mod școală</div>
                            <div className="text-xs text-muted-foreground">
                              B2C = profesorul creează clase și elevi • B2B = secretariatul gestionează operaționalul
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              options={[
                                { value: 'b2c', label: 'B2C' },
                                { value: 'b2b', label: 'B2B' },
                              ]}
                              value={schoolModeDraft}
                              onChange={(v) => setSchoolModeDraft(v)}
                              disabled={savingSchoolMode}
                            />
                            <Button
                              size="sm"
                              iconName="Save"
                              onClick={() => void saveSchoolMode()}
                              disabled={savingSchoolMode || schoolModeDraft === (schoolSettings?.school_mode || 'b2c')}
                            >
                              Salvează
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="space-y-4">
                          <div className="border border-border rounded-lg p-3 bg-background">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="font-medium text-foreground">Utilizatori</div>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="RefreshCw"
                              onClick={() => void loadSelectedSchoolDetails()}
                            >
                              Reîncarcă
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 gap-2 mb-3">
                            <Input
                              label="Caută"
                              placeholder="Nume / email..."
                              value={schoolUsersQ}
                              onChange={(e) => setSchoolUsersQ(e.target.value)}
                            />
                            <Select
                              label="Rol"
                              value={schoolUsersRole}
                              options={roleOptions}
                              onChange={(v) => setSchoolUsersRole(String(v))}
                              searchable
                              clearable
                            />
                            <div>
                              <Button
                                variant="outline"
                                iconName="Search"
                                onClick={() => void loadSelectedSchoolDetails()}
                              >
                                Aplică
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-2 max-h-[360px] overflow-auto pr-1">
                            {schoolUsers.length === 0 ? (
                              <div className="text-sm text-muted-foreground">Nu există utilizatori.</div>
                            ) : null}
                            {schoolUsers.map((u) => (
                              <div
                                key={u.id}
                                className="border border-border rounded-lg p-3 flex items-start justify-between gap-3"
                              >
                                <div>
                                  <div className="font-medium text-foreground">
                                    {u.full_name}
                                    {u.is_platform_admin ? (
                                      <span className="text-xs text-primary ml-2 align-middle">(platformă)</span>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {u.email} • {u.role}
                                    {u.is_suspended ? ' • suspendat' : ''}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <input
                                      type="checkbox"
                                      checked={isGrantEnabled('user', Number(u.id))}
                                      onChange={(e) => void setGrant('user', Number(u.id), e.target.checked)}
                                      disabled={savingGrant}
                                    />
                                    Licență
                                  </label>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName={u.is_suspended ? 'Play' : 'Pause'}
                                    onClick={() => void suspendToggle(u)}
                                  >
                                    {u.is_suspended ? 'Activează' : 'Suspendă'}
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    iconName="Trash2"
                                    onClick={() => void deleteUser(u)}
                                  >
                                    Șterge
                                  </Button>
                                </div>
                              </div>
                              ))}
                          </div>
                        </div>

                        <div className="border border-border rounded-lg p-3 bg-background">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="font-medium text-foreground">Conturi secretariat</div>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="RefreshCw"
                              onClick={() => void loadSecretariatUsers()}
                              disabled={secretariatLoading || !selectedSchoolId}
                            >
                              Reîncarcă
                            </Button>
                          </div>
                          <div className="text-xs text-muted-foreground mb-3">
                            Creezi conturi de secretariat pentru școala selectată. Parola temporară se trimite pe email și trebuie schimbată la primul login.
                          </div>

                          <div className="grid grid-cols-1 gap-2 mb-3">
                            <Input
                              label="Nume"
                              placeholder="Secretariat 1"
                              value={secretariatNewName}
                              onChange={(e) => setSecretariatNewName(e.target.value)}
                            />
                            <Input
                              label="Email"
                              placeholder="secretariat@scoala.ro"
                              value={secretariatNewEmail}
                              onChange={(e) => setSecretariatNewEmail(e.target.value)}
                            />
                            <div>
                              <Button
                                variant="default"
                                iconName="UserPlus"
                                onClick={() => void createSecretariatUser()}
                                loading={secretariatCreateLoading}
                                disabled={!selectedSchoolId}
                              >
                                Creează
                              </Button>
                            </div>
                          </div>

                          {secretariatLoading ? (
                            <div className="text-sm text-muted-foreground">Se încarcă…</div>
                          ) : secretariatUsers.length === 0 ? (
                            <div className="text-sm text-muted-foreground">Nu există conturi de secretariat.</div>
                          ) : (
                            <div className="space-y-2 max-h-[240px] overflow-auto pr-1">
                              {secretariatUsers.map((u) => (
                                <div
                                  key={u.id}
                                  className="border border-border rounded-lg p-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-foreground break-words sm:truncate">{u.full_name}</div>
                                    <div className="text-xs text-muted-foreground break-all sm:truncate">{u.email}</div>
                                    {u.is_suspended ? <div className="text-xs text-destructive mt-1">Suspendat</div> : null}
                                  </div>
                                  <div className="flex flex-wrap items-center justify-end gap-2 w-full sm:w-auto">
                                    {u.is_suspended ? (
                                      <Button variant="outline" size="sm" iconName="Play" onClick={() => void activateSecretariatUser(u)}>
                                        Activează
                                      </Button>
                                    ) : null}
                                    {!u.is_suspended ? (
                                      <Button variant="outline" size="sm" iconName="Pause" onClick={() => void suspendSecretariatUser(u)}>
                                        Suspendă
                                      </Button>
                                    ) : null}
                                    <Button variant="outline" size="sm" iconName="Send" onClick={() => void resendSecretariatInvite(u)}>
                                      Retrimite
                                    </Button>
                                    <Button variant="destructive" size="sm" iconName="Trash2" onClick={() => void deleteSecretariatUser(u)}>
                                      Șterge
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="border border-border rounded-lg p-3 bg-background">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="font-medium text-foreground">Admini școală</div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="UserPlus"
                                onClick={() => setCreateSchoolAdminOpen(true)}
                                disabled={!selectedSchoolId}
                              >
                                Creează
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="RefreshCw"
                                onClick={() => void loadSchoolAdmins()}
                                disabled={schoolAdminsLoading || !selectedSchoolId}
                              >
                                Reîncarcă
                              </Button>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground mb-3">
                            Creezi conturi de admin pentru școala selectată. Poți retrimite invitația (se resetează parola).
                          </div>

                          {schoolAdminsLoading ? (
                            <div className="text-sm text-muted-foreground">Se încarcă…</div>
                          ) : schoolAdmins.length === 0 ? (
                            <div className="text-sm text-muted-foreground">Nu există admini de școală.</div>
                          ) : (
                            <div className="space-y-2 max-h-[240px] overflow-auto pr-1">
                              {schoolAdmins.map((u) => (
                                <div
                                  key={u.id}
                                  className="border border-border rounded-lg p-3 flex items-start justify-between gap-3"
                                >
                                  <div className="min-w-0">
                                    <div className="font-medium text-foreground truncate">
                                      {u.full_name}
                                      {u.is_suspended ? (
                                        <span className="text-xs text-error ml-2 align-middle">(suspendat)</span>
                                      ) : null}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {u.is_suspended ? (
                                      <Button variant="outline" size="sm" iconName="Play" onClick={() => void activateSchoolAdmin(u)}>
                                        Activează
                                      </Button>
                                    ) : (
                                      <Button variant="outline" size="sm" iconName="Pause" onClick={() => void suspendSchoolAdmin(u)}>
                                        Suspendă
                                      </Button>
                                    )}
                                    <Button variant="outline" size="sm" iconName="Send" onClick={() => void resendSchoolAdminInvite(u)}>
                                      Retrimite
                                    </Button>
                                    <Button variant="destructive" size="sm" iconName="Trash2" onClick={() => void deleteSchoolAdmin(u)}>
                                      Șterge
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="border border-border rounded-lg p-3 bg-background">
                        <div className="font-medium text-foreground mb-2">Clase</div>
                        <div className="text-xs text-muted-foreground mb-3">
                            Sistemul are 2 tipuri: „Clase (catalog)” și „Classrooms” (pentru cursuri/comunitate).
                          </div>

                          <div className="mb-4 border border-border rounded-lg p-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div>
                                <div className="text-sm font-semibold text-foreground">Licență școală (fără restricții)</div>
                                <div className="text-xs text-muted-foreground">Dezactivează toate restricțiile de abonament pentru utilizatorii din școală.</div>
                              </div>
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={isGrantEnabled('school', Number(selectedSchoolId))}
                                  onChange={(e) => void setGrant('school', Number(selectedSchoolId), e.target.checked)}
                                  disabled={savingGrant}
                                />
                                <span className="text-muted-foreground">Activ</span>
                              </label>
                            </div>
                          </div>

                          <div className="mb-4">
                            <div className="text-sm font-semibold text-foreground mb-2">Clase (catalog)</div>
                            <div className="space-y-2 max-h-[180px] overflow-auto pr-1">
                              {(schoolClasses?.school_classes || []).length === 0 ? (
                                <div className="text-sm text-muted-foreground">Nu există clase (catalog).</div>
                              ) : null}
                              {(schoolClasses?.school_classes || []).map((c) => (
                                <div
                                  key={c.id}
                                  className="border border-border rounded-lg p-3 flex items-center justify-between gap-3"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm text-foreground">{c.name}</div>
                                    <div className="mt-2 space-y-1">
                                      <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-muted-foreground">Rating</span>
                                        <span className="font-medium text-foreground">
                                          {typeof c.rating_percent === 'number' ? `${c.rating_percent}%` : '—'}
                                        </span>
                                      </div>
                                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                        <div
                                          className={`h-full transition-all duration-300 ${typeof c.rating_percent === 'number' ? 'bg-primary' : 'bg-muted'}`}
                                          style={{ width: `${typeof c.rating_percent === 'number' ? c.rating_percent : 0}%` }}
                                          role="progressbar"
                                          aria-valuenow={typeof c.rating_percent === 'number' ? c.rating_percent : 0}
                                          aria-valuemin={0}
                                          aria-valuemax={100}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <input
                                      type="checkbox"
                                      checked={isGrantEnabled('school_class', Number(c.id))}
                                      onChange={(e) => void setGrant('school_class', Number(c.id), e.target.checked)}
                                      disabled={savingGrant}
                                    />
                                    Licență
                                  </label>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    iconName="Trash2"
                                    onClick={() => void deleteSchoolClass(Number(c.id))}
                                  >
                                    Șterge
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <div className="text-sm font-semibold text-foreground mb-2">Classrooms</div>
                            <div className="space-y-2 max-h-[180px] overflow-auto pr-1">
                              {(schoolClasses?.classrooms || []).length === 0 ? (
                                <div className="text-sm text-muted-foreground">Nu există classrooms.</div>
                              ) : null}
                              {(schoolClasses?.classrooms || []).map((c) => (
                                <div
                                  key={c.id}
                                  className="border border-border rounded-lg p-3 flex items-center justify-between gap-3"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm text-foreground">{c.name}</div>
                                    <div className="text-[11px] text-muted-foreground mt-1">
                                      Admini:{' '}
                                      {(c.admins || []).length > 0
                                        ? (c.admins || []).map((a) => a.full_name).join(', ')
                                        : c.admin_id != null
                                          ? `ID ${c.admin_id}`
                                          : '—'}
                                    </div>
                                  </div>
                                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <input
                                      type="checkbox"
                                      checked={isGrantEnabled('classroom', Number(c.id))}
                                      onChange={(e) => void setGrant('classroom', Number(c.id), e.target.checked)}
                                      disabled={savingGrant}
                                    />
                                    Licență
                                  </label>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    iconName="Trash2"
                                    onClick={() => void deleteClassroom(Number(c.id))}
                                  >
                                    Șterge
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Selectează o școală din stânga pentru detalii (utilizatori, clase, acțiuni).
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {tab === 'users' ? (
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div className="font-semibold text-foreground">Utilizatori (global)</div>
                  <Button
                    variant="outline"
                    iconName="RefreshCw"
                    onClick={() => void loadGlobalUsers()}
                    disabled={loading}
                  >
                    Reîncarcă
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                  <Input
                    label="Caută"
                    placeholder="Nume / email..."
                    value={globalUsersQ}
                    onChange={(e) => setGlobalUsersQ(e.target.value)}
                  />
                  <Select
                    label="Rol"
                    value={globalUsersRole}
                    options={roleOptions}
                    onChange={(v) => setGlobalUsersRole(String(v))}
                    searchable
                    clearable
                  />
                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      iconName="Search"
                      onClick={() => {
                        setGlobalUsersPage(1);
                        void loadGlobalUsers();
                      }}
                    >
                      Caută
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  {globalUsers.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Nu există utilizatori.</div>
                  ) : null}

                  {globalUsers.map((u) => {
                    const isOpen = Boolean(globalUserDetailsOpen[Number(u.id)]);
                    const isDetailsLoading = Boolean(globalUserDetailsLoading[Number(u.id)]);
                    const details = globalUserDetails[Number(u.id)] || null;
                    const du = details?.user || null;
                    const legalStatus = details?.legal?.status || {};
                    const recentLogins: any[] = Array.isArray(details?.logins?.recent) ? details.logins.recent : [];
                    const legalConsents: any[] = Array.isArray(details?.legal?.consents) ? details.legal.consents : [];
                    return (
                      <div key={u.id} className="border border-border rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="font-medium text-foreground">
                              {u.full_name}
                              {u.is_platform_admin ? <span className="text-xs text-primary ml-2">(platformă)</span> : null}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {u.email} • {u.role}
                              {u.is_suspended ? ' • suspendat' : ''}
                              {u.school_name ? ` • ${u.school_name} (${u.county}, ${u.city})` : ''}
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span
                                className={`text-[11px] px-2 py-1 rounded border ${
                                  u.subscription_unrestricted
                                    ? 'bg-primary/10 border-primary/30 text-primary'
                                    : u.subscription_plan === 'full'
                                      ? 'bg-success/10 border-success/30 text-success'
                                      : u.subscription_plan === 'basic'
                                        ? 'bg-warning/10 border-warning/30 text-warning'
                                        : 'bg-muted border-border text-foreground'
                                }`}
                                title={
                                  u.subscription_expires_at
                                    ? `Expiră: ${new Date(u.subscription_expires_at).toLocaleString('ro-RO')}`
                                    : ''
                                }
                              >
                                {u.subscription_unrestricted ? 'LICENȚĂ' : String((u.subscription_plan || 'free') as any).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              iconName={isOpen ? 'ChevronUp' : 'ChevronDown'}
                              onClick={() => {
                                const next = !isOpen;
                                setGlobalUserDetailsOpen((p) => ({ ...p, [Number(u.id)]: next }));
                                if (next && !details) void loadGlobalUserDetails(Number(u.id));
                              }}
                            >
                              Detalii
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName={u.is_suspended ? 'Play' : 'Pause'}
                              onClick={() => void suspendToggle(u)}
                            >
                              {u.is_suspended ? 'Activează' : 'Suspendă'}
                            </Button>
                            <Button variant="destructive" size="sm" iconName="Trash2" onClick={() => void deleteUser(u)}>
                              Șterge
                            </Button>
                          </div>
                        </div>

                        {isOpen ? (
                          <div className="mt-3 border-t border-border pt-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="text-sm font-medium text-foreground">Detalii utilizator</div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="Download"
                                  onClick={() => void exportGlobalUserAudit(Number(u.id))}
                                  disabled={isDetailsLoading}
                                >
                                  Export JSON
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="RefreshCw"
                                  onClick={() => void loadGlobalUserDetails(Number(u.id))}
                                  disabled={isDetailsLoading}
                                >
                                  Reîncarcă
                                </Button>
                              </div>
                            </div>

                            {isDetailsLoading ? (
                              <div className="text-sm text-muted-foreground mt-2">Se încarcă...</div>
                            ) : details ? (
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
                                <div className="border border-border rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-2">Date personale</div>
                                  <div className="text-sm text-foreground">Email: {du?.email || '—'}</div>
                                  <div className="text-sm text-foreground">Username: {du?.username || '—'}</div>
                                  <div className="text-sm text-foreground">Telefon: {du?.phone || '—'}</div>
                                  <div className="text-sm text-foreground">Adresă: {du?.address || '—'}</div>
                                  <div className="text-sm text-foreground">Creat: {du?.created_at ? new Date(du.created_at).toLocaleString('ro-RO') : '—'}</div>
                                </div>

                                <div className="border border-border rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-2">Securitate & acces</div>
                                  <div className="text-sm text-foreground">Email confirmat: {du?.email_confirmed ? 'Da' : 'Nu'}</div>
                                  <div className="text-sm text-foreground">Suspendat: {du?.is_suspended ? 'Da' : 'Nu'}</div>
                                  <div className="text-sm text-foreground">Schimbă parola: {du?.must_change_password ? 'Da' : 'Nu'}</div>
                                  <div className="text-sm text-foreground">Prima login: {du?.first_login_at ? new Date(du.first_login_at).toLocaleString('ro-RO') : '—'}</div>
                                  <div className="text-sm text-foreground">Ultima login: {du?.last_login_at ? new Date(du.last_login_at).toLocaleString('ro-RO') : '—'}</div>
                                  <div className="text-sm text-foreground">Număr login: {Number(du?.login_count || 0)}</div>
                                </div>

                                <div className="border border-border rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-2">Legal</div>
                                  <div className="text-sm text-foreground">Termeni: {legalStatus?.terms_accepted ? 'Acceptat' : 'Neacceptat'}</div>
                                  <div className="text-sm text-foreground">Confidențialitate: {legalStatus?.privacy_accepted ? 'Acceptat' : 'Neacceptat'}</div>
                                  <div className="text-sm text-foreground">Cookies: {legalStatus?.cookies_accepted ? 'Acceptat' : 'Neacceptat'}</div>
                                  <div className="text-xs text-muted-foreground mt-2">Ultime accepturi (hash + IP)</div>
                                  <div className="mt-2 space-y-1 max-h-[140px] overflow-auto pr-1">
                                    {legalConsents.length === 0 ? (
                                      <div className="text-xs text-muted-foreground">Nu există accepturi.</div>
                                    ) : null}
                                    {legalConsents.slice(0, 10).map((c: any) => (
                                      <div key={c.id} className="text-xs text-muted-foreground">
                                        {String(c.document || '').toUpperCase()} • {c.ip || '—'} •{' '}
                                        {c.created_at ? new Date(c.created_at).toLocaleString('ro-RO') : '—'}
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="border border-border rounded-lg p-3 lg:col-span-3">
                                  <div className="text-xs text-muted-foreground mb-2">Logări recente (IP)</div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {recentLogins.length === 0 ? (
                                      <div className="text-xs text-muted-foreground">Nu există logări înregistrate.</div>
                                    ) : null}
                                    {recentLogins.slice(0, 10).map((e: any) => (
                                      <div key={e.id} className="border border-border rounded p-2 text-xs">
                                        <div className="text-foreground">
                                          {e.occurred_at ? new Date(e.occurred_at).toLocaleString('ro-RO') : '—'} • {String(e.method || '').toUpperCase()}
                                        </div>
                                        <div className="text-muted-foreground">
                                          IP: {e.ip || '—'} • {e.success ? 'OK' : `Eroare: ${e.error || '—'}`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground mt-2">Nu am găsit detalii.</div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}

                  <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
                    <div className="text-xs text-muted-foreground">
                      Total: {globalUsersTotal} • Pagina {globalUsersPage} / {Math.max(1, Math.ceil(globalUsersTotal / globalUsersPageSize))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setGlobalUsersPage((p) => Math.max(1, p - 1))}
                        disabled={loading || globalUsersPage <= 1}
                      >
                        Înapoi
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setGlobalUsersPage((p) => {
                            const last = Math.max(1, Math.ceil(globalUsersTotal / globalUsersPageSize));
                            return Math.min(last, p + 1);
                          })
                        }
                        disabled={
                          loading ||
                          globalUsersPage >= Math.max(1, Math.ceil(globalUsersTotal / globalUsersPageSize))
                        }
                      >
                        Înainte
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'email' ? <EmailCampaignsPanel /> : null}

            {tab === 'contact' ? <ContactSubmissionsPanel /> : null}

            {tab === 'reports' ? (
              <div className="space-y-4">
                <TrafficReportsPanel />
                <PaymentsReportsPanel />
              </div>
            ) : null}

            {tab === 'settings' ? (
              <PlatformSettingsPanel />
            ) : null}
          </div>
        </main>

        {createSchoolOpen ? (
          <Modal title="Adaugă școală" subtitle="Creează o școală în platformă." onClose={() => setCreateSchoolOpen(false)}>
            <div className="grid grid-cols-1 gap-3">
              <Input
                label="Nume"
                placeholder="Ex: Școala Gimnazială Nr. 1"
                value={createSchoolName}
                onChange={(e) => setCreateSchoolName(e.target.value)}
              />
              <Select
                label="Forma de învățământ"
                placeholder="Selectează forma de învățământ"
                value={createSchoolEducationLevel}
                options={educationLevelOptions}
                onChange={(v) => setCreateSchoolEducationLevel(v ? String(v) : '')}
                clearable
              />
              <Select
                label="Județ"
                value={createSchoolCounty}
                options={counties.map((c) => ({ value: c, label: c }))}
                onChange={(v) => {
                  const next = v ? String(v) : '';
                  setCreateSchoolCounty(next);
                  setCreateSchoolCity('');
                  if (!next) setLocalities([]);
                }}
                searchable
                clearable
                creatable
              />
              <Select
                label="Localitate"
                placeholder="Selectează localitate"
                options={localities.map((l) => ({ value: l, label: l }))}
                value={createSchoolCity}
                onChange={(v) => setCreateSchoolCity(v ? String(v) : '')}
                disabled={!createSchoolCounty.trim()}
                loading={isLoadingLocalities}
                searchable
                clearable
                creatable
                createLabel={(term) => `Folosește localitatea: "${term}"`}
              />
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setCreateSchoolOpen(false)}>
                Renunță
              </Button>
              <Button variant="default" iconName="Plus" onClick={() => void createSchool()}>
                Creează
              </Button>
            </div>
          </Modal>
        ) : null}

        {createSchoolAdminOpen ? (
          <Modal
            title="Creează administrator de școală"
            subtitle={
              <>
                Contul va avea rol <span className="font-medium">admin</span> și va fi asociat școlii selectate.
              </>
            }
            onClose={() => setCreateSchoolAdminOpen(false)}
          >
            <div className="grid grid-cols-1 gap-3">
              <Input
                label="Nume (opțional)"
                placeholder="Ex: Administrator Școală"
                value={schoolAdminName}
                onChange={(e) => setSchoolAdminName(e.target.value)}
              />
              <Input
                label="Email"
                placeholder="admin@scoala.ro"
                value={schoolAdminEmail}
                onChange={(e) => setSchoolAdminEmail(e.target.value)}
              />
              <Input
                label="Parolă"
                placeholder="Se genereaza automat (trimis pe email)"
                value={''}
                onChange={() => {}}
                type="password"
                disabled
              />
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setCreateSchoolAdminOpen(false)}>
                Renunță
              </Button>
              <Button variant="default" iconName="UserPlus" onClick={() => void createSchoolAdmin()}>
                Creează cont
              </Button>
            </div>
          </Modal>
        ) : null}
      </div>
    </>
  );
}
